import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ANALYTICAL_VARIABLE_WRITE_MUTATION_KEY } from '@verantos-dev/core-constants';
import type { ListResponse, ObjectResponse } from '@verantos-dev/types-common';

import { authFetch } from './fetchers';
import { avKeys } from './queryKeys';
import type { AnalyticalVariableDetail } from './useAnalyticalVariableDetailQuery';
import type { AnalyticalVariableListItem } from './useAnalyticalVariableListQuery';

export interface RenameAvMutationInput {
  analyticalVariableId: string;
  dataElement: string;
  /**
   * Project the rename was triggered from, when known. ``projectId``
   * participates in the ``libraryDetail`` cache key (OR-6081 AC22), so
   * omitting it here while the detail page is open under a project route
   * would optimistically patch a cache slot nothing is reading. Also the
   * only way to reach the project artifact list's optimistic patch below.
   * Omit when there's no active project surface (e.g. the org-wide AV
   * library page).
   */
  projectId?: string;
}

/**
 * Mirrors ``projectPageKeys.artifactsForProject`` (feature-project-page's
 * ``queryKeys.ts``) BY VALUE, not by import — feature-analytical-variables
 * can't depend on feature-project-page (build cycle; see
 * ``useProjectPageAnalyticalVariableSync`` for the reverse-direction bridge
 * on this same pair). TanStack keys are identity-by-value on purpose (that
 * file's own docstring calls this out), so a literal tuple here collides
 * with that package's key.
 */
const projectArtifactsKey = (projectId: string) => ['projectPage', 'artifacts', projectId] as const;

/** The minimal shape this hook reads/writes from a project-artifact-list
 * cache entry — narrower than feature-project-page's full
 * ``ProjectArtifactEntry`` union, which this package can't import. */
interface ProjectArtifactListEntryShape {
  id: string;
  kind: string;
  metadata: { dataElement?: string | null } | null;
}

interface MutationContext {
  detailKey: readonly unknown[];
  /**
   * Snapshot of the libraryDetail cache entry before we optimistically
   * overwrote it. Used by ``onError`` to restore the cache when the
   * server rejects the mutation. ``undefined`` when there was no prior
   * cache entry — we delete the slot on rollback rather than restore to
   * undefined.
   */
  previousDetail: ObjectResponse<AnalyticalVariableDetail> | undefined;
  /** Every ``avKeys.library()``-prefixed query's data before the patch —
   * covers the AV list page and the cohort-builder / dataset "Add
   * variable" pickers, each cached under a distinct searchParams key. */
  previousLists: Array<[readonly unknown[], unknown]>;
  /** The project artifact list's data before the patch, when ``projectId``
   * was supplied. */
  previousProjectArtifacts: readonly [readonly unknown[], unknown] | undefined;
}

const patchAvListItem = (
  old: ListResponse<AnalyticalVariableListItem> | undefined,
  analyticalVariableId: string,
  dataElement: string
): ListResponse<AnalyticalVariableListItem> | undefined => {
  // The broad avKeys.library() prefix also matches libraryDetail entries
  // (a single-object shape, not a list) — pass those through untouched
  // rather than assuming an array.
  if (!old || !Array.isArray(old.data)) return old;
  let changed = false;
  const data = old.data.map((item) => {
    if (item.analyticalVariableId !== analyticalVariableId) return item;
    changed = true;
    return { ...item, dataElement };
  });
  return changed ? { ...old, data } : old;
};

const patchProjectArtifactsEntry = (
  old: ListResponse<ProjectArtifactListEntryShape> | undefined,
  analyticalVariableId: string,
  dataElement: string
): ListResponse<ProjectArtifactListEntryShape> | undefined => {
  if (!old || !Array.isArray(old.data)) return old;
  let changed = false;
  const data = old.data.map((entry) => {
    if (entry.kind !== 'av' || entry.id !== analyticalVariableId || !entry.metadata) return entry;
    changed = true;
    return { ...entry, metadata: { ...entry.metadata, dataElement } };
  });
  return changed ? { ...old, data } : old;
};

/**
 * Mutation hook for renaming an analytical variable's display name
 * (OR-6090). Shared by all three rename surfaces (AV list page,
 * project artifact list, variable detail page) — each supplies its own
 * UI (dialog or inline editor) around this one call.
 *
 * Calls ``PATCH /analytical-variables/{id}`` with ``{ dataElement }``.
 * Server enforces creator-only authorization (403).
 *
 * Optimistic-with-rollback across every cache slot that renders this
 * variable's name, so there's no visible lag before the invalidation
 * below lands: the detail cache, every active AV-list query (library
 * page + pickers), and — when ``projectId`` is supplied — the project
 * artifact list. ``onSettled``'s root-prefix invalidation (plus the
 * mutation-key-tagged ``useProjectPageAnalyticalVariableSync`` for the
 * project list) is the authoritative refetch; the optimistic patch is
 * purely to close the gap until that refetch resolves.
 */
export const useRenameAvMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ObjectResponse<AnalyticalVariableDetail>,
    Error,
    RenameAvMutationInput,
    MutationContext
  >({
    mutationKey: [ANALYTICAL_VARIABLE_WRITE_MUTATION_KEY],
    mutationFn: ({ analyticalVariableId, dataElement }) =>
      authFetch<ObjectResponse<AnalyticalVariableDetail>>(
        `/analytical-variables/${analyticalVariableId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ dataElement }),
        }
      ),
    onMutate: async ({ analyticalVariableId, dataElement, projectId }) => {
      const detailKey = avKeys.libraryDetail(analyticalVariableId, projectId ?? '');
      const projectKey = projectId ? projectArtifactsKey(projectId) : undefined;

      await Promise.all([
        queryClient.cancelQueries({ queryKey: detailKey }),
        queryClient.cancelQueries({ queryKey: avKeys.library() }),
        ...(projectKey ? [queryClient.cancelQueries({ queryKey: projectKey })] : []),
      ]);

      // Snapshot every slot BEFORE patching any of them. avKeys.library() is
      // a prefix of detailKey, so getQueriesData({queryKey: avKeys.library()})
      // would also return detailKey's entry — capturing it after the detail
      // patch below would snapshot the already-optimistic value and poison
      // the rollback.
      const previousDetail =
        queryClient.getQueryData<ObjectResponse<AnalyticalVariableDetail>>(detailKey);
      const previousLists = queryClient.getQueriesData<ListResponse<AnalyticalVariableListItem>>({
        queryKey: avKeys.library(),
      });
      const previousProjectArtifacts: readonly [readonly unknown[], unknown] | undefined =
        projectKey ? ([projectKey, queryClient.getQueryData(projectKey)] as const) : undefined;

      if (previousDetail?.data) {
        queryClient.setQueryData<ObjectResponse<AnalyticalVariableDetail>>(detailKey, {
          ...previousDetail,
          data: { ...previousDetail.data, dataElement },
        });
      }
      queryClient.setQueriesData<ListResponse<AnalyticalVariableListItem>>(
        { queryKey: avKeys.library() },
        (old) => patchAvListItem(old, analyticalVariableId, dataElement)
      );
      if (projectKey) {
        queryClient.setQueryData<ListResponse<ProjectArtifactListEntryShape>>(projectKey, (old) =>
          patchProjectArtifactsEntry(old, analyticalVariableId, dataElement)
        );
      }

      return { detailKey, previousDetail, previousLists, previousProjectArtifacts };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      if (context.previousDetail !== undefined) {
        queryClient.setQueryData(context.detailKey, context.previousDetail);
      } else {
        queryClient.removeQueries({ queryKey: context.detailKey });
      }
      context.previousLists.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      if (context.previousProjectArtifacts) {
        const [key, data] = context.previousProjectArtifacts;
        queryClient.setQueryData(key, data);
      }
    },
    // No onSuccess cache write: the PATCH response is shaped as
    // AnalyticalVariableSchema (no ``source``), but ``libraryDetail`` may be
    // populated by useVariableDetail's VariableDetail shape (``source`` +
    // knowledge-only fields) — overwriting it here would strip those fields
    // until the refetch below lands, flickering the rename affordance off.
    // onMutate's optimistic patch (a spread over the existing cache entry)
    // already reflects the new name in the correct shape.
    onSettled: () => {
      // Root-prefix invalidation for every analytical-variable-keyed
      // surface (AV list page, detail page, cohort-builder picker). The
      // project artifact list is under a separate key root and is
      // reached instead via the ``mutationKey`` tag above (AC6).
      void queryClient.invalidateQueries({ queryKey: avKeys.all });
    },
  });
};
